import type { FieldValue } from 'firebase/firestore';
import {
  collection,
  query,
  where,
  getDocs,
  orderBy,
  addDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  getDoc,
} from 'firebase/firestore';
import { db } from '../../infrastructure/firebase/config';
import type { Workout, CreateWorkoutInput, MonthlySummary } from '../../domain/workouts/types';
import { buildMonthlySummary } from '../../domain/workouts/monthlyBests';
import {
  getMonthKey,
  generateIdempotencyKey,
  getCurrentMonthKey,
} from '../../domain/workouts/types';
import { scoreWorkout } from '../../domain/scoring';
import { normalizeCategory } from '../../domain/scoring/categories';

export async function createWorkout(input: CreateWorkoutInput): Promise<Workout> {
  const scoreResult = scoreWorkout({
    bodyWeightLb: input.bodyWeightLb,
    scoringSex: input.scoringSex,
    category: input.category,
    enteredWeightLb: input.enteredWeightLb,
    reps: input.reps,
    inputWeightMultiplier: input.inputWeightMultiplier,
    normalizationMultiplier: input.normalizationMultiplier,
    bodyWeightFactor: input.bodyWeightFactor,
  });

  const monthKey = getMonthKey(input.workoutDate);
  const idempotencyKey = generateIdempotencyKey();

  const workoutFields = {
    userId: input.userId,
    workoutDate: input.workoutDate,
    monthKey,
    category: input.category,
    exerciseId: input.exerciseId,
    exerciseNameSnapshot: input.exerciseName,

    enteredWeightLb: input.enteredWeightLb,
    reps: input.reps,
    inputWeightMultiplier: input.inputWeightMultiplier,
    bodyWeightFactor: input.bodyWeightFactor ?? 0,

    estimated1RmLb: scoreResult.estimated1RmLb,
    normalizationMultiplier: input.normalizationMultiplier,
    normalized1RmLb: scoreResult.normalized1RmLb,

    bodyWeightLbSnapshot: input.bodyWeightLb,
    scoringSexSnapshot: input.scoringSex,
    dotsCoefficient: scoreResult.dotsCoefficient,
    categoryReferenceDots: scoreResult.categoryReferenceDots,
    points: scoreResult.points,

    scoringVersion: scoreResult.scoringVersion,
    exerciseConfigVersion: input.exerciseConfigVersion,

    note: input.note ?? '',
    idempotencyKey,
  };

  // Firestore payload includes server timestamps (FieldValue, not Date)
  const firestorePayload: typeof workoutFields & { createdAt: FieldValue; updatedAt: FieldValue } =
    {
      ...workoutFields,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

  const workoutsRef = collection(db, 'workouts');
  const docRef = await addDoc(workoutsRef, firestorePayload);

  const now = new Date();
  return {
    id: docRef.id,
    ...workoutFields,
    createdAt: now,
    updatedAt: now,
  };
}

export async function getMonthlyBests(userId: string, monthKey: string): Promise<MonthlySummary> {
  const workouts = await getUserWorkouts(userId, monthKey);
  return buildMonthlySummary(workouts);
}

async function fetchWithRetry<T>(fn: () => Promise<T>, retries = 2): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    if (retries > 0) {
      await new Promise((r) => setTimeout(r, 400));
      return fetchWithRetry(fn, retries - 1);
    }
    throw err;
  }
}

export async function getUserWorkouts(userId: string, monthKey: string): Promise<Workout[]> {
  const workoutsRef = collection(db, 'workouts');
  const q = query(workoutsRef, where('userId', '==', userId), where('monthKey', '==', monthKey));

  const querySnapshot = await fetchWithRetry(() => getDocs(q));

  const list = querySnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
    // Workouts written before the hinge rename store category 'pull'.
    category: normalizeCategory(doc.data().category),
    createdAt: doc.data().createdAt?.toDate?.() || new Date(),
    updatedAt: doc.data().updatedAt?.toDate?.() || new Date(),
  })) as Workout[];

  return list.sort((a, b) => new Date(b.workoutDate).getTime() - new Date(a.workoutDate).getTime());
}

/**
 * Every workout a user has ever logged, newest first. Used by the progress
 * charts to build the list of exercises the user has actually performed.
 * Backed by the userId + workoutDate index.
 */
export async function getAllUserWorkouts(userId: string): Promise<Workout[]> {
  const workoutsRef = collection(db, 'workouts');
  const q = query(workoutsRef, where('userId', '==', userId), orderBy('workoutDate', 'desc'));

  const querySnapshot = await fetchWithRetry(() => getDocs(q));

  return querySnapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
    category: normalizeCategory(doc.data().category),
    createdAt: doc.data().createdAt?.toDate?.() || new Date(),
    updatedAt: doc.data().updatedAt?.toDate?.() || new Date(),
  })) as Workout[];
}

export async function deleteWorkout(workoutId: string): Promise<void> {
  const workoutRef = doc(db, 'workouts', workoutId);
  const workoutDoc = await getDoc(workoutRef);

  if (!workoutDoc.exists()) {
    throw new Error('Workout not found');
  }

  const data = workoutDoc.data();
  const currentMonth = getCurrentMonthKey();

  if (data.monthKey !== currentMonth) {
    throw new Error('Only current month workouts can be deleted');
  }

  await deleteDoc(workoutRef);
}

/**
 * Deletes any workout, of any age, with none of deleteWorkout's guards.
 *
 * Named apart from deleteWorkout on purpose: that one deliberately refuses
 * anything outside the current month so a user cannot quietly rewrite their own
 * history. This is the admin escape hatch and should only ever be called from
 * the admin panel.
 */
export async function adminDeleteWorkout(workoutId: string): Promise<void> {
  await deleteDoc(doc(db, 'workouts', workoutId));
}
